import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { 
  SidebarComponent, 
  SidebarMenuItem, 
  SidebarCallToAction, 
  SidebarFooterItem 
} from '../../shared/components/sidebar/sidebar.component';
import { AuthApiService } from '@angular-workspace/services';

interface Organisation {
  status?: 'pending' | 'granted' | 'rejected';
  request_type?: string;
  orgName?: string;
  entityType?: string;
  entitySector?: string;
  websiteUrl?: string;
  address?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-consumer-sidebar',
  standalone: true,
  imports: [CommonModule, SidebarComponent],
  templateUrl: './consumer-sidebar.component.html',
  styleUrl: './consumer-sidebar.component.scss',
})
export class ConsumerSidebarComponent implements OnInit {
  organisation: Organisation | null = null;
  organisationStatus: 'pending' | 'granted' | null = null;
  organisationName: string | null = null;
  loading = true;

  menuItems: SidebarMenuItem[] = [];

  // callToAction: SidebarCallToAction | undefined = {
  //   title: 'Join an Organisation',
  //   description: 'Register or Join an Organisation on the NHA to begin contributing Datasets, Models, Usecases.',
  //   linkText: 'Explore Further',
  //   linkRoute: '/consumer/join-organisation',
  //   icon: '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 2C15 6 15 18 12 22C9 18 9 6 12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 2C9 6 9 18 12 22C15 18 15 6 12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  // };
  callToAction: SidebarCallToAction | undefined = undefined;

  constructor(
    private authApiService: AuthApiService,
    private route: ActivatedRoute
  ) {}

  async ngOnInit(): Promise<void> {
    // After withdraw, we navigate with ?withdrawn=1 – show "Join an Organisation" immediately
    if (this.route.snapshot.queryParamMap.get('withdrawn') === '1') {
      this.clearOrganisationState();
      return;
    }
    await this.loadUserOrganisation();
    this.updateMenuItems();
    this.updateCallToAction();
  }

  /** Clear organisation state so sidebar shows "Join an Organisation" (e.g. after withdraw). */
  clearOrganisationState(): void {
    this.organisation = null;
    this.organisationStatus = null;
    this.organisationName = null;
    this.loading = false;
    this.updateMenuItems();
    this.updateCallToAction();
  }

  async loadUserOrganisation(): Promise<void> {
    try {
      this.loading = true;
      const userInfo = await this.authApiService.getUserInfo();
      
      // Extract organisation from user API response
      // The organisation might be in result.organisation, result.org, or directly in result
      const result = userInfo['result'] || userInfo;
      const org = result['organisation'] || result['organization'] || result['org'] || null;
      
      // Extract organisation name from result (it's at root level, not in org object)
      this.organisationName = result['organisationName'] || result['organizationName'] || null;
      
      if (org && org.status) {
        this.organisation = org;
        // Set status only if it's 'pending' or 'granted'
        this.organisationStatus = org.status === 'pending' || org.status === 'granted' ? org.status : null;
        
        // Debug: Log organisation data to verify it's being extracted correctly
        console.log('Organisation data:', {
          status: org.status,
          request_type: org.request_type || org['request_type'] || org['requestType'],
          organisation: this.organisation
        });
      } else {
        this.organisation = null;
        this.organisationStatus = null;
      }
    } catch (error) {
      console.error('Error loading user organisation:', error);
      this.organisation = null;
      this.organisationStatus = null;
      this.organisationName = null;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Refresh sidebar from API, or optimistically clear organisation (e.g. after withdraw).
   * @param clearOrganisation When true, clear org state immediately so "Join an Organisation" shows without waiting for API.
   */
  async refreshOrganisationStatus(clearOrganisation?: boolean): Promise<void> {
    if (clearOrganisation) {
      this.clearOrganisationState();
      return;
    }
    await this.loadUserOrganisation();
    this.updateMenuItems();
    this.updateCallToAction();
  }

  updateMenuItems(): void {
    // Base menu items
    const baseMenuItems: SidebarMenuItem[] = [
      {
        label: 'Dashboard',
        route: '/consumer/home',
        icon: '<path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      },
      {
        label: 'My Submissions',
        route: '/consumer/my-submissions',
        icon: '<path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 2V8H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 13H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 17H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 9H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      },
      // {
      //   label: 'My Request',
      //   route: '/consumer/requests',
      //   icon: '<path d="M4 7C4 6.46957 4.21071 5.96086 4.58579 5.58579C4.96086 5.21071 5.46957 5 6 5H14L18 9V17C18 17.5304 17.7893 18.0391 17.4142 18.4142C17.0391 18.7893 16.5304 19 16 19H6C5.46957 19 4.96086 18.7893 4.58579 18.4142C4.21071 18.0391 4 17.5304 4 17V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 5V9H18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 7C7 6.46957 7.21071 5.96086 7.58579 5.58579C7.96086 5.21071 8.46957 5 9 5H17L21 9V17C21 17.5304 20.7893 18.0391 20.4142 18.4142C20.0391 18.7893 19.5304 19 19 19H9C8.46957 19 7.96086 18.7893 7.58579 18.4142C7.21071 18.0391 7 17.5304 7 17V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M17 5V9H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      // },
      // {
      //   label: 'My Projects',
      //   route: '/consumer/my-projects',
      //   icon: '<path d="M4 7C4 6.46957 4.21071 5.96086 4.58579 5.58579C4.96086 5.21071 5.46957 5 6 5H10L12 7H18C18.5304 7 19.0391 7.21071 19.4142 7.58579C19.7893 7.96086 20 8.46957 20 9V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      // },
      // {
      //   label: 'Delegate',
      //   route: '/consumer/delegate',
      //   icon: '<path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="8.5" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M17 11L22 16L17 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 16H12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      // },
    ];

    // Check if organisation is granted and request_type is organisation_join
    const isOrganisationGranted = 
      this.organisationStatus === 'granted' && 
      this.organisation && 
      (this.organisation.request_type === 'organisation_join' || 
       this.organisation['request_type'] === 'organisation_join' ||
       this.organisation['requestType'] === 'organisation_join') &&
      this.organisationName;

    // Add Organisation menu item if conditions are met
    if (isOrganisationGranted) {
      this.menuItems = [
        ...baseMenuItems,
        {
          label: 'Organisation',
          route: '/consumer/data-provider-entity',
          icon: '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 2C15 6 15 18 12 22C9 18 9 6 12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
        },
      ];
    } else {
      this.menuItems = baseMenuItems;
    }
  }

  updateCallToAction(): void {
    // Check if there's a pending organisation_join request
    const isPendingOrganisationJoin = 
      this.organisationStatus === 'pending' && 
      this.organisation && 
      (this.organisation.request_type === 'organisation_join' || 
       this.organisation['request_type'] === 'organisation_join' ||
       this.organisation['requestType'] === 'organisation_join');
    
    // Check if there's a pending organisation_create request
    const isPendingOrganisationCreate = 
      this.organisationStatus === 'pending' && 
      this.organisation && 
      (this.organisation.request_type === 'organisation_create' || 
       this.organisation['request_type'] === 'organisation_create' ||
       this.organisation['requestType'] === 'organisation_create');
    
    if (isPendingOrganisationJoin || isPendingOrganisationCreate) {
      // If organisation object exists, show data-provider-entity-form (view-only)
      // If no organisation object, show create-organisation stepper
      const hasOrganisation = this.organisation && Object.keys(this.organisation).length > 0;
      const targetRoute = hasOrganisation 
        ? '/consumer/data-provider-entity' 
        : '/consumer/create-organisation';
      
      // Show "Awaiting Approval" message - make it clickable to navigate to appropriate page
      this.callToAction = {
        title: 'Awaiting Approval',
        description: 'Your request will be approved by the organisation manager. shortly !!',
        linkText: '',
        linkRoute: targetRoute,
        icon: '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      };
    } else if (
      this.organisationStatus === 'granted' && 
      this.organisation && 
      (this.organisation.request_type === 'organisation_join' || 
       this.organisation['request_type'] === 'organisation_join' ||
       this.organisation['requestType'] === 'organisation_join') &&
      this.organisationName
    ) {
      // Organisation is granted - hide callToAction (Organisation will be shown as menu item)
      this.callToAction = undefined;
    } else {
      // Join an Organisation CTA hidden for TANUH consumer panel
      this.callToAction = undefined;
      // this.callToAction = {
      //   title: 'Join an Organisation',
      //   description: 'Register or Join an Organisation on the NHA to begin contributing Datasets, Models, Usecases.',
      //   linkText: 'Explore Further',
      //   linkRoute: '/consumer/join-organisation',
      //   icon: '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 2C15 6 15 18 12 22C9 18 9 6 12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 2C9 6 9 18 12 22C15 18 15 6 12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      // };
    }
  }

  footerItems: SidebarFooterItem[] = [
    {
      label: 'Profile',
      route: '/consumer/profile',
      // Person icon (head and shoulders)
      icon: '<path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    },
    {
      label: 'Support',
      route: '/consumer/support',
      // Two overlapping figures (support/people icon)
      icon: '<path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    },
  ];
}

